import { useState } from "react";
import { CheckBox } from "../widgets/CheckBox";
import { ColumnAlign } from "../widgets/ColumnAlign";
import { IpaKeyboard } from "../widgets/IpaKeyboard";
import { IpaButtons } from "../widgets/IpaButtons";
import { AssignKeys } from "./AssignKeys";
import { PersistOrtho } from "./persistOrtho";
import { KeyMap } from "./keyToOrthoMapping";
import { download } from "../../persist/download";
import { keyMapToVersionString } from "./versionFiles";
import { ButtonStatusType, StatusButton } from "../widgets/StatusButton";

/** UI for entering text using a custom orthography & keyboard */
export function EnterOrtho(props: {
  onChangeText: (s: string) => void,
  setAssignment: (keystroke: string, symbols: string[]) => void,
  moveKey: (keystroke: string, where: 'up' | 'down') => void,
  deleteKey: (keystroke: string) => void,
  clearAll: () => void,
  keyListChanged: () => void,
  keyNameChanged: (name: string) => void,
  getKeyHelp: (keystroke: string) => string | string[],
  onSpeak: () => void,
  all: Record<string, string[]>, /** all key -> ortho symbols mappings */
  single: Record<string, string>, /** key -> ortho symbol */
  multi: Record<string, string[]>, /** key -> multiple ortho symbols */
  keys: KeyMap,
  name: string,
  width: number,
}) {
  const [saveStatus, setSaveStatus] = useState<ButtonStatusType>('disabled')
  const [showAssignments, setShowAssignments] = useState(false);
  const [showOrthoKeyboard, setOrthoKeyboard] = useState(true);
  const [insert, setInsert] = useState<[string] | undefined>();
  const [inputText, setInputText] = useState('');
  const [lastName, setLastName] = useState<string | null>(null);

  if (props.name !== lastName) {
    // the outside pushed a new set of keys, so don't offer to save
    setSaveStatus('disabled');
    setLastName(props.name);
  }

  // intercept all events to track the save status
  function onSetAssignment(keystroke: string, symbols: string[]) {
    props.setAssignment(keystroke, symbols);
    const disabled = props.name === '' || Object.getOwnPropertyNames(props.keys).length === 0;
    setSaveStatus(disabled ? 'disabled' : 'pending');
  }
  function onMoveKey(keystroke: string, where: 'up' | 'down') {
    props.moveKey(keystroke, where);
    const disabled = props.name === '';
    setSaveStatus(disabled ? 'disabled' : 'pending');
  }
  function onDeleteKey(keystroke: string) {
    props.deleteKey(keystroke);
    const disabled = props.name === '' || Object.getOwnPropertyNames(props.keys).length <= 1;
    setSaveStatus(disabled ? 'disabled' : 'pending');
  }
  function onClearAll() {
    props.clearAll();
    setSaveStatus('disabled');
  }
  function onKeyListChanged() {
    props.keyListChanged();
    const disabled = props.name === '' || Object.getOwnPropertyNames(props.keys).length === 0;
    setSaveStatus(disabled ? 'disabled' : 'pending');
  }
  function onKeyNameChanged(name: string) {
    props.keyNameChanged(name);
    setLastName(name);  // so we can tell when the outside pushes a new set of keys on us
    const disabled = name === '' || Object.getOwnPropertyNames(props.keys).length === 0;
    setSaveStatus(disabled ? 'disabled' : 'pending');
  }
  function onSave() {
    setSaveStatus('success');
  }

  /** user changed the input text */
  function changeInputText(newText: string) {
    setInputText(newText);
    props.onChangeText(newText);
  }
  function changeShowKeyboard(checked: boolean) {
    setOrthoKeyboard(checked);
    document.getElementById('ortho-input')?.focus();
  }

  return (<>
    <ColumnAlign width={props.width}>{[
      <span key={1}>input text:</span>,
      <span key={2}>
        <CheckBox checked={showAssignments} onChange={setShowAssignments}>assign keys</CheckBox>
      </span>,
      <span key={3}>
        <CheckBox checked={showOrthoKeyboard} onChange={changeShowKeyboard}>show custom keyboard</CheckBox>
      </span>,
    ]}</ColumnAlign>
    {showAssignments
      ? <>
        <SaveAndClearKeys saveStatus={saveStatus} onSave={onSave}
          assignmentMap={props.all} keys={props.keys} clearAll={onClearAll} keyListChanged={onKeyListChanged}
          name={props.name} keyNameChanged={onKeyNameChanged} />
        <AssignKeys assignmentMap={props.all} setAssignment={onSetAssignment} moveKey={onMoveKey} deleteKey={onDeleteKey} />
      </>
      : <></>}
    {showOrthoKeyboard
      ? <IpaButtons onClick={(s) => setInsert([s])} single={props.single} multi={props.multi} descriptions={props.getKeyHelp} width={props.width} />
      : <></>}
    <IpaKeyboard id='ortho-input' cols={80} rows={5} onSpeak={props.onSpeak}
      value={inputText} onChange={changeInputText} single={props.single} multi={props.multi} insert={insert} />
  </>);
}

/** options for clearing all keys, saving/deleting key assignments */
function SaveAndClearKeys(props: {
  readonly saveStatus: ButtonStatusType,
  readonly onSave: () => void,
  readonly assignmentMap: Record<string, string[]>,
  readonly keys: KeyMap,
  readonly clearAll: () => void,
  readonly keyListChanged: () => void,
  readonly name: string,
  readonly keyNameChanged: (name: string) => void,
}) {
  const [persist] = useState(new PersistOrtho());

  const assigned = props.assignmentMap;
  const all = Object.getOwnPropertyNames(assigned);

  function saveKeys() {
    if (props.name === '') return;
    persist.saveKeys(props.name, props.keys);
    props.keyListChanged();
    props.onSave();
  }
  function deleteKeys() {
    if (props.name === '') return;
    persist.deleteKeys(props.name);
    props.keyListChanged();
  }
  function downloadKeys() {
    const data = keyMapToVersionString(props.keys);
    if (data.length > 0) {
      const filename = props.name === '' ? 'fonim-keys.txt' : `${props.name}-keys.txt`;
      download(filename, data);
    }
  }
  function clearKeys() {
    props.clearAll();
    props.keyNameChanged('');
  }

  return (<div style={{ display: "flex", justifyContent: "center" }}>
    <ColumnAlign width={420}>{[
      <span key={1}>
        <StatusButton status={props.saveStatus} onClick={saveKeys}>save</StatusButton>
        <input type="text" size={12} value={props.name} onChange={(e) => props.keyNameChanged(e.target.value)} />
        <button onClick={deleteKeys} disabled={props.name === ''}>delete</button>
      </span>,
      <span key={2}>
        <button onClick={downloadKeys}>download</button>
      </span>,
      <span key={3}>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <button onClick={clearKeys} disabled={all.length === 0}>clear keys</button><br />
      </span>,
    ]}</ColumnAlign>
  </div>);
}
